import { Box, Button, Grid } from '@mui/material'
import { sendGet, sendPost } from 'hooks/use-fetch'
import { useEffect, useState } from 'react'
import { SelectWithLabel, type SelectOption } from 'Components/SelectWithLabel'
import { toast } from 'react-toastify'
import { ClassroomRosterReportTableView } from './ClassroomRosterReportTableView'

export interface ClassroomRosterReportModel {
  firstName: string
  lastName: string
  dateOfBirth: string | null
  address: string | null
  city: string | null
  state: string | null
  zipCode: string | null
  fplQuintile: number | null
  caregiverName: string | null
  caregiverLastName: string | null
  email: string | null
  primaryPhone: string | null
  developmentalDelay: number
  challengingBehavior: number
  primaryLanguageNotEnglish: number
  parentLowEducation: number
  abuseNeglect: number
}

export function ClassroomRosterReport (): JSX.Element {
  const [loading, setLoading] = useState(false)
  const [reportData, setReportData] = useState<ClassroomRosterReportModel[]>([])
  const [showResults, setShowResults] = useState(false)

  const [facilityRooms, setFacilityRooms] = useState<Array<SelectOption<number>>>([])
  const [selectedFacilityRoomID, setSelectedFacilityRoomID] = useState<number | undefined>(undefined)

  const [programs, setPrograms] = useState<Array<SelectOption<number>>>([])
  const [selectedProgramID, setSelectedProgramID] = useState<number | undefined>(undefined)

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      // Fetch ISD Programs
      const { response } = await sendGet('/Reporting/ISDPrograms', {})
      setPrograms(response)

      // Fetch Facility Rooms
      const { response: roomsResponse } = await sendGet('/Reporting/FacilityRooms', {})
      if (roomsResponse != null && Array.isArray(roomsResponse)) {
        setFacilityRooms(roomsResponse)
      }
    }

    void fetchData()
  }, [])

  const generateReport = async (): Promise<void> => {
    if (selectedFacilityRoomID === undefined) {
      toast.error('Please select a Classroom.')
      return
    }

    if (selectedProgramID === undefined) {
      toast.error('Please select a Program.')
      return
    }

    const options = {
      facilityRoomID: selectedFacilityRoomID,
      isdProgramID: selectedProgramID
    }

    setLoading(true)
    try {
      const { response, success } = await sendPost('/Report/ClassroomRosterReport/GetClassroomRosterReport', options)
      if (success) {
        setReportData(response)
        setShowResults(true)
      } else {
        toast.error('Failed to generate report.')
      }
    } catch (error) {
      toast.error('An error occurred while generating the report.')
      console.error(error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Grid container columns={7} gap={1} sx={{ display: 'flex', flexDirection: 'row' }}>
        <Grid item xs={3}>
          <SelectWithLabel
            includeNoneOption={false}
            name="facilityRoomID"
            label="Classroom"
            value={selectedFacilityRoomID}
            width="100%"
            options={facilityRooms}
            onChange={(e) => { setSelectedFacilityRoomID(Number(e.target.value)) }}
          />
        </Grid>
        <Grid item xs={3}>
          <SelectWithLabel
            includeNoneOption={false}
            name="isdProgramID"
            label="Program"
            value={selectedProgramID}
            width="100%"
            options={programs}
            onChange={(e) => { setSelectedProgramID(Number(e.target.value)) }}
          />
        </Grid>
      </Grid>

      <Box>
        <Button
          color='secondary'
          variant='contained'
          onClick={generateReport}
          disabled={loading}
        >
          {loading ? 'Generating...' : 'Generate Report'}
        </Button>
      </Box>

      {showResults &&
        <Box>
          <ClassroomRosterReportTableView reportData={reportData} loading={loading} />
        </Box>
      }
    </>
  )
}
